import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Mail, Check, X, Plus } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsletterForm = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [genres, setGenres] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [customCities, setCustomCities] = useState([]);
  const [newCustomCity, setNewCustomCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      const [genresRes, citiesRes] = await Promise.all([
        axios.get(`${API}/genres`),
        axios.get(`${API}/cities`)
      ]);
      setGenres(genresRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const toggleGenre = (value) => {
    setSelectedGenres(prev => 
      prev.includes(value) 
        ? prev.filter(g => g !== value)
        : [...prev, value]
    );
  };

  const toggleCity = (value) => {
    setSelectedCities(prev => 
      prev.includes(value) 
        ? prev.filter(c => c !== value)
        : [...prev, value]
    );
  };

  const addCustomCity = () => {
    const city = newCustomCity.trim();
    if (city && !customCities.includes(city)) {
      setCustomCities(prev => [...prev, city]);
      setNewCustomCity('');
    }
  };

  const removeCustomCity = (city) => {
    setCustomCities(prev => prev.filter(c => c !== city));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Por favor, introduce tu email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email,
        genres: selectedGenres,
        cities: selectedCities,
        custom_cities: customCities
      });
      // Debug + update local member if logged in
      try {
        const stored = localStorage.getItem('memberData');
        console.log("memberData (antes):", stored);
        if (stored) {
          const m = JSON.parse(stored);
          const updated = { ...m, accept_communications: true };
          localStorage.setItem('memberData', JSON.stringify(updated));
          console.log("memberData (después):", updated);
        } else {
          console.log("No hay memberData (usuario no logueado).");
        }
        // Dispara siempre para que el dashboard pueda refrescar si está abierto
        window.dispatchEvent(new Event('memberUpdate'));
        console.log("memberUpdate dispatched");
      } catch (e) {
        console.log("ERROR al actualizar memberData:", e);
      }
      setSuccess(true);
      toast.success('¡Suscripción exitosa!');
    } catch (error) {
      console.log("Error subscribe:", error?.response?.data || error);
      toast.error('Error al suscribirse');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 bg-[var(--primary)]/20 flex items-center justify-center">
          <Check size={32} className="text-[var(--primary)]" />
        </div>
        <h3 className="font-['Playfair_Display'] text-2xl text-white mb-2">
          ¡Gracias por suscribirte!
        </h3>
        <p className="text-white/50">
          Te notificaremos sobre nuevos eventos según tus preferencias.
        </p>
        {onClose && (
          <Button onClick={onClose} className="btn-outline mt-6">
            Cerrar
          </Button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="newsletter-form">
      <div className="text-center mb-6">
        <Mail size={32} className="mx-auto text-[var(--primary)] mb-4" />
        <h3 className="font-['Playfair_Display'] text-2xl text-white mb-2">
          Suscríbete al Newsletter
        </h3>
        <p className="text-white/50 text-sm">
          Recibe notificaciones sobre nuevos eventos de tus géneros y ciudades favoritas.
        </p>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
          Email *
        </label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tu@email.com"
          className="bg-transparent border-white/20 text-white rounded-none"
          required
          data-testid="newsletter-email"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/50 mb-3 block">
          Géneros de interés
        </label>
        <div className="grid grid-cols-2 gap-2">
          {genres.map((genre) => (
            <label
              key={genre.value}
              className={`flex items-center gap-2 p-3 border cursor-pointer transition-all ${
                selectedGenres.includes(genre.value)
                  ? 'border-[var(--primary)] bg-[var(--primary)]/10'
                  : 'border-white/10 hover:border-white/20'
              }`}
            >
              <Checkbox
                checked={selectedGenres.includes(genre.value)}
                onCheckedChange={() => toggleGenre(genre.value)}
                className="border-white/30 data-[state=checked]:bg-[var(--primary)] data-[state=checked]:border-[var(--primary)]"
              />
              <span className="text-white/70 text-sm">{genre.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/50 mb-3 block">
          Ciudades de interés
        </label>
        <div className="grid grid-cols-2 gap-2">
          {cities.map((city) => (
            <label
              key={city.value}
              className={`flex items-center gap-2 p-3 border cursor-pointer transition-all ${
                selectedCities.includes(city.value)
                  ? 'border-[var(--primary)] bg-[var(--primary)]/10'
                  : 'border-white/10 hover:border-white/20'
              }`}
            >
              <Checkbox
                checked={selectedCities.includes(city.value)}
                onCheckedChange={() => toggleCity(city.value)}
                className="border-white/30 data-[state=checked]:bg-[var(--primary)] data-[state=checked]:border-[var(--primary)]"
              />
              <span className="text-white/70 text-sm">{city.label}</span>
            </label>
          ))}
        </div>

        {/* Custom Cities Section */}
        <div className="mt-4">
          <label className="text-xs text-white/40 mb-2 block">
            ¿Tu ciudad no está en la lista? Añádela:
          </label>
          <div className="flex gap-2">
            <Input
              type="text"
              value={newCustomCity}
              onChange={(e) => setNewCustomCity(e.target.value)}
              placeholder="Escribe tu ciudad..."
              className="bg-transparent border-white/20 text-white rounded-none flex-1"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomCity())}
            />
            <Button
              type="button"
              onClick={addCustomCity}
              variant="outline"
              className="btn-outline px-3"
              disabled={!newCustomCity.trim()}
            >
              <Plus size={18} />
            </Button>
          </div>
          
          {/* Custom Cities Tags */}
          {customCities.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {customCities.map((city) => (
                <span
                  key={city}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-[var(--primary)]/20 text-[var(--primary)] text-sm"
                >
                  {city}
                  <button
                    type="button"
                    onClick={() => removeCustomCity(city)}
                    className="hover:text-white ml-1"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <Button
        type="submit"
        className="w-full btn-primary"
        disabled={loading}
        data-testid="newsletter-submit"
      >
        {loading ? 'Suscribiendo...' : 'Suscribirse'}
      </Button>

      <p className="text-white/30 text-xs text-center">
        Puedes darte de baja en cualquier momento.
      </p>
    </form>
  );
};

export default NewsletterForm;
